import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import FractionInput from "../../../../components/inputs/FractionInput";
import { isCorrectNumber, pick, randInt, renderPreview, taskTags } from "./helpers";

function factorLatex({ a, b }) {
  const xPart = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  const sign = b >= 0 ? "+" : "-";
  return `(${xPart} ${sign} ${Math.abs(b)})`;
}

function productLatex(coefficient, factors) {
  const coef = coefficient === 1 ? "" : coefficient === -1 ? "-" : String(coefficient);
  return `${coef}${factors.map(factorLatex).join("")}`;
}

function createTaskData() {
  const count = randInt(3, 4);
  const coefficient = pick([-3, -2, 2, 3]);
  const factors = Array.from({ length: count }, (_, index) => ({
    a: index === 0 ? pick([1, -1]) : pick([-3, -2, -1, 1, 2, 3]),
    b: pick([-5, -4, -3, -2, 2, 3, 4, 5]),
  }));

  return {
    coefficient,
    factors,
    degree: count,
    leadingCoefficient: factors.reduce((product, factor) => product * factor.a, coefficient),
    freeTerm: factors.reduce((product, factor) => product * factor.b, coefficient),
  };
}

export function generateTask() {
  return {
    type: "PolynomialProductFeaturesTask",
    points: 1,
    data: createTaskData(),
    tags: taskTags(["stopien_wielomianu", "iloczyn_czynnikow"]),
  };
}

export function renderTask(task) {
  return (
    <>
      <p>Bez wykonywania pełnego mnożenia wyznacz stopień, współczynnik przy najwyższej potędze i wyraz wolny wielomianu.</p>
      <BlockMath math={`P(x)=${productLatex(task.data.coefficient, task.data.factors)}`} />
    </>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const update = (key, value) => onChange({ ...(userAnswer || {}), [key]: value });
  return (
    <div className="task-input-stack">
      <FractionInput label="\\deg P" value={userAnswer?.degree ?? ""} onChange={(value) => update("degree", value)} disabled={disabled} placeholder="np. 3" panelWidthDesktop={180} />
      <FractionInput label="a_n" value={userAnswer?.leadingCoefficient ?? ""} onChange={(value) => update("leadingCoefficient", value)} disabled={disabled} placeholder="np. -2" panelWidthDesktop={180} />
      <FractionInput label="a_0" value={userAnswer?.freeTerm ?? ""} onChange={(value) => update("freeTerm", value)} disabled={disabled} placeholder="np. 12" panelWidthDesktop={180} />
    </div>
  );
}

export function checkAnswer(task, userAnswer) {
  const degree = isCorrectNumber(userAnswer?.degree, task.data.degree);
  const leading = isCorrectNumber(userAnswer?.leadingCoefficient, task.data.leadingCoefficient);
  const free = isCorrectNumber(userAnswer?.freeTerm, task.data.freeTerm);
  const correct = degree && leading && free;
  return { correct, points: correct ? 1 : 0 };
}

export function renderAnswer(task) {
  return (
    <span>
      <InlineMath math={`\\deg P=${task.data.degree}`} />, <InlineMath math={`a_n=${task.data.leadingCoefficient}`} />, <InlineMath math={`a_0=${task.data.freeTerm}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer) {
  return (
    <span>
      {renderPreview(userAnswer?.degree, "\\deg P")}, {renderPreview(userAnswer?.leadingCoefficient, "a_n")}, {renderPreview(userAnswer?.freeTerm, "a_0")}
    </span>
  );
}

export function renderExplanation(task) {
  const leadingProduct = [task.data.coefficient, ...task.data.factors.map((factor) => factor.a)].join("\\cdot ");
  const freeProduct = [task.data.coefficient, ...task.data.factors.map((factor) => factor.b)].join("\\cdot ");
  return (
    <div>
      <p><strong>Krok 1.</strong> Stopień iloczynu liniowych czynników jest równy liczbie tych czynników.</p>
      <BlockMath math={`\\deg P=${task.data.degree}`} />
      <p><strong>Krok 2.</strong> Współczynnik przy najwyższej potędze dostajemy z iloczynu współczynników stojących przy zmiennej.</p>
      <BlockMath math={`a_n=${leadingProduct}=${task.data.leadingCoefficient}`} />
      <p><strong>Krok 3.</strong> Wyraz wolny dostajemy z iloczynu wyrazów wolnych wszystkich czynników i współczynnika przed nawiasami.</p>
      <BlockMath math={`a_0=${freeProduct}=${task.data.freeTerm}`} />
      <p><strong>Odpowiedź:</strong> {renderAnswer(task)}.</p>
    </div>
  );
}
